/**
 * Floating Category Labels — premium "scattered islands" composition.
 *
 * Story-Beat: "Heute getrennt." — die Allyver-Branchen leben heute jede für
 * sich auf eigenen Apps und Plattformen. Visuell wird das als ruhig
 * schwebende Kategorie-Pills inszeniert, die um den frei stehenden Headline-
 * Block treiben.
 *
 * Jede Pill ist mit einem QUADRATISCHEN APP-ICON verheiratet, das die heute
 * dominante App der jeweiligen Branche visuell zitiert (Lieferando, Amazon,
 * Uber, Booking.com, Eventim, MyHammer, Kleinanzeigen). Damit liest sich die
 * Szene unmittelbar als „7 Branchen = heute 7 verschiedene Apps".
 *
 * Designprinzipien:
 *   - Headline und Subtext stehen FREI (kein Kasten, kein Glas-Frame).
 *     Nur ein diffuser Halo dahinter klärt die visuelle Hierarchie.
 *   - Alle Pills haben das EXAKT gleiche Design (Form, Padding, Schriftgröße)
 *     — nur die Farbe variiert nach Preismodell:
 *       · 0% Provision  → Lila (Restaurants, Shops)
 *       · 12€ Flatrate → Türkis (Taxis, Hotels, Events, Dienstleister,
 *         Inserate)
 *   - Item-Format akzeptiert "Titel | tier | appKey" (tier = "0%" oder
 *     "12€"; appKey z.B. "lieferando", "amazon", … — wenn nicht gesetzt,
 *     wird er automatisch aus dem Titel abgeleitet).
 *   - Bewegung: nur sanftes vertikales Schweben, KEIN Drehen, KEIN 3D-Tilt.
 *   - Positionen sind handkuratiert für 9:16 — Pills bleiben in der
 *     Safe-Zone und überschneiden den Headline-Bereich (~y 42-58%) nicht.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
} from "../../reel/sceneShared.js";

type Tier = "free" | "flat";

type Slot = {
  left: number;
  top: number;
  /** Vertikale Drift-Amplitude in px (-= nach oben, += nach unten). */
  driftY: number;
  /** Phasenversatz der Schwebe-Bewegung (0..1, in Sekunden), damit nicht
   *  alle Pills synchron auf-/abwippen. */
  phase: number;
  delay: number;
};

/**
 * Handkuratierte Slots für 7 Units (Icon + Pill) im 9:16-Frame.
 *
 * Mit den hinzugekommenen quadratischen App-Icons über jeder Pill ist die
 * Floating-Unit jetzt ~140 px hoch (statt vorher ~36 px). Die Slot-Reihen
 * wurden deshalb leicht nach außen geschoben, damit:
 *   - die obere Reihe nicht in die Top-Safezone rutscht,
 *   - die mittleren Slots nicht in den Headline-Korridor (~y 40-62%)
 *     hineinwandern,
 *   - die untere Reihe nicht über die Bottom-Safezone hinausragt.
 *
 * Horizontal sitzen die Slots immer noch bewusst nahe der Mitte, damit die
 * Units als zusammengehöriger Cluster um die Headline lesen.
 */
const PORTRAIT_SLOTS_7: Slot[] = [
  { left: 30, top: 16, driftY: -11, phase: 0.00, delay: 0.00 },
  { left: 70, top: 20, driftY:  10, phase: 0.40, delay: 0.08 },
  { left: 22, top: 33, driftY:   9, phase: 0.80, delay: 0.16 },
  { left: 78, top: 35, driftY: -10, phase: 0.20, delay: 0.24 },
  { left: 24, top: 68, driftY:  11, phase: 0.60, delay: 0.32 },
  { left: 76, top: 70, driftY:  -9, phase: 0.10, delay: 0.40 },
  { left: 50, top: 84, driftY:  10, phase: 0.50, delay: 0.48 },
];

const WIDE_SLOTS_7: Slot[] = [
  { left: 18, top: 26, driftY: -11, phase: 0.00, delay: 0.00 },
  { left: 42, top: 16, driftY:  10, phase: 0.40, delay: 0.08 },
  { left: 82, top: 22, driftY:   9, phase: 0.80, delay: 0.16 },
  { left: 14, top: 60, driftY: -10, phase: 0.20, delay: 0.24 },
  { left: 86, top: 52, driftY:  11, phase: 0.60, delay: 0.32 },
  { left: 32, top: 82, driftY:  -9, phase: 0.10, delay: 0.40 },
  { left: 68, top: 80, driftY:  10, phase: 0.50, delay: 0.48 },
];

function fallbackSlots(n: number, isVertical: boolean): Slot[] {
  const out: Slot[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    const rx = isVertical ? 22 : 30;
    const ry = isVertical ? 28 : 26;
    out.push({
      left: 50 + Math.cos(angle) * rx,
      top: 50 + Math.sin(angle) * ry,
      driftY: i % 2 === 0 ? -10 : 10,
      phase: (i * 0.3) % 1,
      delay: i * 0.08,
    });
  }
  return out;
}

function pickSlots(n: number, isVertical: boolean): Slot[] {
  if (n === 7) return isVertical ? PORTRAIT_SLOTS_7 : WIDE_SLOTS_7;
  return fallbackSlots(n, isVertical);
}

/**
 * Parsed ein Item ("Restaurants | 0%" / "Taxis | 12€ | uber" /
 * "Restaurants") in Label + Tier + optionalen App-Key.
 *   - "0%", "0", "free", "provision" → free
 *   - "12€", "12", "flat", "flatrate" → flat
 *   - kein Suffix → default (flat → Türkis)
 *   - Dritter Slot ist ein optionaler App-Key, der den Auto-Mapping-
 *     Mechanismus überschreibt (z.B. "lieferando", "amazon").
 */
function parseItem(raw: string): { label: string; tier: Tier; appKey?: string } {
  const parts = raw.split("|");
  const label = (parts[0] ?? "").trim();
  const tier = (parts[1] ?? "").trim().toLowerCase();
  const appKey = (parts[2] ?? "").trim().toLowerCase() || undefined;
  const tierKind: Tier =
    tier.includes("0%") || tier === "0" || tier === "free" || tier.includes("provision")
      ? "free"
      : "flat";
  return { label, tier: tierKind, appKey };
}

/* --------------------------------------------------------------------- */
/*  App-Icons — visuelle Zitate der heute dominanten Branchen-Apps.       */
/* --------------------------------------------------------------------- */

/**
 * Mapping: Kategorie-Label → App-Key. Wird angewendet, wenn das Item keinen
 * expliziten dritten Slot ("Restaurants | 0% | lieferando") liefert.
 */
const APP_FOR_LABEL: Record<string, string> = {
  restaurants: "lieferando",
  restaurant: "lieferando",
  food: "lieferando",
  lieferung: "lieferando",
  delivery: "lieferando",
  shops: "amazon",
  shop: "amazon",
  shopping: "amazon",
  produkte: "amazon",
  händler: "amazon",
  haendler: "amazon",
  haendlerinnen: "amazon",
  retail: "amazon",
  taxi: "uber",
  taxis: "uber",
  ride: "uber",
  fahrt: "uber",
  hotel: "booking",
  hotels: "booking",
  unterkunft: "booking",
  unterkünfte: "booking",
  unterkuenfte: "booking",
  event: "eventim",
  events: "eventim",
  tickets: "eventim",
  veranstaltung: "eventim",
  veranstaltungen: "eventim",
  dienstleister: "myhammer",
  dienstleistungen: "myhammer",
  service: "myhammer",
  services: "myhammer",
  handwerk: "myhammer",
  inserate: "kleinanzeigen",
  inserat: "kleinanzeigen",
  ads: "kleinanzeigen",
  kleinanzeigen: "kleinanzeigen",
};

/**
 * Liefert HTML für ein quadratisches App-Icon (iOS-Squircle-Look) mit der
 * Farbgebung der jeweiligen realen App. Es geht NICHT um Pixel-Genauigkeit
 * (das wäre Markenrechts-Murks), sondern um "auf den ersten Blick erkenne
 * ich welche App da steht": korrekte Farbe + zentrales Glyph/Logo-Hint.
 */
function appIconHtml(key: string, size: number): string {
  const radius = Math.round(size * 0.235);
  const base =
    `position:relative;width:${size}px;height:${size}px;border-radius:${radius}px;` +
    `display:flex;align-items:center;justify-content:center;flex-shrink:0;` +
    `overflow:hidden;` +
    `box-shadow:0 10px 28px rgba(0,0,0,0.55),0 2px 6px rgba(0,0,0,0.4),inset 0 1px 0 rgba(255,255,255,0.22),inset 0 -1px 0 rgba(0,0,0,0.18);`;

  switch (key) {
    case "lieferando": {
      // Lieferando: kräftiges Orange, weißes Haus mit Besteck-Streifen.
      const glyph = Math.round(size * 0.62);
      return `<div style="${base}background:linear-gradient(180deg,#FF8A00 0%,#FF5C00 100%);">
        <svg width="${glyph}" height="${glyph}" viewBox="0 0 32 32" fill="none" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.18));">
          <path d="M4 15 16 5l12 10v12H4z" fill="#fff"/>
          <rect x="10" y="14.5" width="2.4" height="9.5" rx="1.2" fill="#FF6F00"/>
          <rect x="14.8" y="14.5" width="2.4" height="9.5" rx="1.2" fill="#FF6F00"/>
          <rect x="19.6" y="14.5" width="2.4" height="9.5" rx="1.2" fill="#FF6F00"/>
        </svg>
      </div>`;
    }
    case "amazon": {
      // Amazon (Shopping-App ab 2024): Karton-Optik mit blauem Klebeband
      // und dem orangefarbenen Smile-Pfeil.
      const arrowW = Math.round(size * 0.72);
      const arrowH = Math.round(size * 0.32);
      return `<div style="${base}background:linear-gradient(180deg,#F5DCB0 0%,#E2B675 100%);">
        <div style="position:absolute;top:0;left:0;right:0;height:30%;background:linear-gradient(180deg,#3F86C8 0%,#2A6AAF 100%);box-shadow:0 1px 0 rgba(0,0,0,0.18);"></div>
        <div style="position:absolute;top:0;left:0;right:0;height:30%;background-image:repeating-linear-gradient(90deg, rgba(255,255,255,0.18) 0 6px, transparent 6px 14px);opacity:0.45;"></div>
        <svg style="position:absolute;left:50%;bottom:14%;transform:translateX(-50%);" width="${arrowW}" height="${arrowH}" viewBox="0 0 72 24" fill="none">
          <path d="M5 8 C 18 22, 54 22, 64 9" stroke="#FF9900" stroke-width="5.5" stroke-linecap="round" fill="none"/>
          <path d="M59 4 L67 8.5 L57.5 13 Z" fill="#FF9900"/>
        </svg>
      </div>`;
    }
    case "uber": {
      // Uber: schwarz mit weißem Taxi-Glyph. Kein Wordmark mehr — die
      // Silhouette des Wagens kommuniziert die Branche stärker als der
      // Markenname.
      const taxi = Math.round(size * 0.66);
      return `<div style="${base}background:linear-gradient(180deg,#1a1a1a 0%,#000 100%);">
        <svg width="${taxi}" height="${taxi}" viewBox="0 0 32 32" fill="none" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));">
          <rect x="9.5" y="4" width="13" height="3.5" rx="0.6" fill="#FFD400"/>
          <rect x="13" y="2.5" width="6" height="2" rx="0.5" fill="#FFD400"/>
          <path d="M5 19 L7 11 H25 L27 19 Z" fill="#fff"/>
          <rect x="4" y="18" width="24" height="7" rx="2" fill="#fff"/>
          <rect x="8.5" y="12.5" width="6.5" height="5" rx="0.8" fill="#1a1a1a"/>
          <rect x="17" y="12.5" width="6.5" height="5" rx="0.8" fill="#1a1a1a"/>
          <rect x="9" y="20.5" width="3" height="2" rx="0.5" fill="#FFD400"/>
          <rect x="20" y="20.5" width="3" height="2" rx="0.5" fill="#1a1a1a"/>
          <circle cx="9" cy="25.5" r="2.6" fill="#1a1a1a" stroke="#fff" stroke-width="0.8"/>
          <circle cx="23" cy="25.5" r="2.6" fill="#1a1a1a" stroke="#fff" stroke-width="0.8"/>
        </svg>
      </div>`;
    }
    case "booking": {
      // Booking.com: Resolution Blue mit weißem "B." (gelber Punkt zitiert
      // den Genius-Akzent). Glyph + Punkt sitzen in einer Inline-Block-Box,
      // die in der äußeren Squircle-Flex-Box exakt mittig ausgerichtet wird.
      // Optische Korrektur: der Punkt verschiebt das Bounding-Box-Zentrum
      // nach rechts, deshalb nudgen wir die ganze Text-Box minimal nach
      // links, damit das "B" optisch im Zentrum sitzt.
      const fs = Math.round(size * 0.62);
      return `<div style="${base}background:linear-gradient(180deg,#0064C8 0%,#003580 100%);color:#fff;font-family:'Outfit',sans-serif;font-weight:900;line-height:1;">
        <span style="display:inline-block;font-size:${fs}px;letter-spacing:-3px;transform:translateX(-${Math.round(fs * 0.06)}px);">B<span style="color:#FFB700;">.</span></span>
      </div>`;
    }
    case "eventim": {
      // Eventim: rote Karte mit weißem Stern (Starfield-Brandmark).
      const star = Math.round(size * 0.62);
      return `<div style="${base}background:linear-gradient(180deg,#E63946 0%,#B81C26 100%);">
        <svg width="${star}" height="${star}" viewBox="0 0 24 24" fill="#fff" style="filter:drop-shadow(0 2px 3px rgba(0,0,0,0.25));">
          <path d="M12 1.5 14.7 9 22.5 9 16.2 13.7 18.5 21 12 16.7 5.5 21 7.8 13.7 1.5 9 9.3 9z"/>
        </svg>
      </div>`;
    }
    case "myhammer": {
      // MyHammer: Dunkelblau mit gelbem Hammer (Markenfarben Blau/Gelb).
      const hammer = Math.round(size * 0.7);
      return `<div style="${base}background:linear-gradient(180deg,#1356A6 0%,#0A2E5C 100%);">
        <svg width="${hammer}" height="${hammer}" viewBox="0 0 32 32" fill="none" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.3));">
          <g transform="rotate(-30 16 16)">
            <rect x="6" y="9.5" width="14" height="6.5" rx="1.2" fill="#FFD400" stroke="#B89200" stroke-width="0.6"/>
            <rect x="18.5" y="11.6" width="9.5" height="2.4" rx="1" fill="#FFD400" stroke="#B89200" stroke-width="0.5"/>
          </g>
        </svg>
      </div>`;
    }
    case "kleinanzeigen": {
      // Kleinanzeigen (2023+): türkisfarbene Karte mit weißem "k". Gleiche
      // Zentrierungs-Logik wie booking — kleiner translateX-Nudge, damit
      // der Punkt das optische Zentrum nicht nach rechts zieht.
      const fs = Math.round(size * 0.62);
      return `<div style="${base}background:linear-gradient(180deg,#34D9D2 0%,#0DAFA8 100%);color:#fff;font-family:'Outfit',sans-serif;font-weight:900;line-height:1;">
        <span style="display:inline-block;font-size:${fs}px;letter-spacing:-2.5px;transform:translateX(-${Math.round(fs * 0.06)}px);">k<span style="color:#FFD400;">.</span></span>
      </div>`;
    }
    default: {
      // Fallback: neutrales Glas-Tile mit Initial.
      return `<div style="${base}background:linear-gradient(180deg,#3a3a44 0%,#1d1d22 100%);color:#fff;font-family:'Outfit',sans-serif;font-weight:900;font-size:${Math.round(size * 0.5)}px;">${key.slice(0, 1).toUpperCase() || "A"}</div>`;
    }
  }
}

function resolveAppKey(label: string, explicit?: string): string {
  if (explicit && explicit.length > 0) return explicit;
  return APP_FOR_LABEL[label.toLowerCase()] ?? "amazon";
}

export const floatingLabels: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const headline = inputs.text ?? "";
  const sub = inputs.subtext ?? "";
  const rawItems = inputs.items?.length
    ? inputs.items
    : [
        "Restaurants | 0%",
        "Shops | 0%",
        "Taxis | 12€",
        "Hotels | 12€",
        "Events | 12€",
        "Dienstleister | 12€",
        "Inserate | 12€",
      ];
  const items = rawItems.slice(0, 9).map(parseItem);

  const isVertical = ctx.format === "9:16";
  const slots = pickSlots(items.length, isVertical);

  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  // Einheitliche, gut lesbare Pill-Schriftgröße. Alle Pills identisch.
  // Etwas kompakter als zuvor, weil über jeder Pill jetzt ein quadratisches
  // App-Icon sitzt — die Gesamt-Unit darf nicht zu klotzig wirken.
  const pillFontPx = isVertical ? Math.round(sm * 0.85) : Math.round(sm * 0.8);
  const pillPadY = isVertical ? 11 : 13;
  const pillPadX = isVertical ? 22 : 28;

  // Quadratische App-Icon-Größe pro Format. Bewusst groß genug, damit das
  // Brand-Glyph erkennbar ist, aber nicht so groß, dass Icons ineinander
  // greifen.
  const iconPx = isVertical ? 80 : 100;

  const turquoise = themeColors.primary; // #22d3c5 — 12€ Flatrate
  const purple = themeColors.secondary;  // #a855f7 — 0% Provision

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const subId = `${clipPrefix}-sub`;
  const haloId = `${clipPrefix}-halo`;
  const pillIds = items.map((_, i) => `${clipPrefix}-pill-${i}`);
  const iconIds = items.map((_, i) => `${clipPrefix}-ic-${i}`);
  const unitIds = items.map((_, i) => `${clipPrefix}-u-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "cinematic", accent: purple });

  /**
   * Eine Floating-Unit besteht aus zwei vertikal gestapelten Elementen:
   *   1) quadratisches App-Icon (oben) — visuelles Zitat der heute
   *      dominanten Branchen-App.
   *   2) Glas-Pill mit Kategorie-Label (unten) — färbt sich nach Tier
   *      (Lila = 0% Provision, Türkis = 12€ Flatrate).
   *
   * Beide bewegen sich gemeinsam (sie teilen den `unit`-Wrapper), damit das
   * sanfte Schweben als geschlossene Karte liest.
   */
  function renderUnit(
    idx: number,
    label: string,
    tier: Tier,
    appKey: string,
    slot: Slot,
  ): string {
    const color = tier === "free" ? purple : turquoise;
    const unitId = unitIds[idx];
    const iconId = iconIds[idx];
    const pillId = pillIds[idx];

    const wrapStyle = [
      "position:absolute",
      `left:${slot.left}%`,
      `top:${slot.top}%`,
      "transform:translate(-50%,-50%)",
      "display:flex",
      "flex-direction:column",
      "align-items:center",
      `gap:${isVertical ? 10 : 12}px`,
      "will-change:transform,opacity",
    ];

    const iconWrap = `<div id="${iconId}" style="will-change:transform,opacity;">${appIconHtml(appKey, iconPx)}</div>`;

    const pillStyle = [
      `padding:${pillPadY}px ${pillPadX}px`,
      "border-radius:9999px",
      "font-family:'Outfit',sans-serif",
      `font-weight:${weights.black}`,
      `font-size:${pillFontPx}px`,
      "letter-spacing:-0.2px",
      "line-height:1",
      "white-space:nowrap",
      "backdrop-filter:blur(14px)",
      "-webkit-backdrop-filter:blur(14px)",
      "will-change:transform,opacity",
      `background:linear-gradient(180deg, ${withAlpha(color, 0.18)} 0%, ${withAlpha(themeColors.bg, 0.55)} 100%)`,
      `color:${themeColors.fg}`,
      `border:1.5px solid ${withAlpha(color, 0.7)}`,
      `box-shadow:0 0 0 1px ${withAlpha(color, 0.18)}, 0 12px 36px ${withAlpha(color, 0.32)}, inset 0 1px 0 ${withAlpha("#ffffff", 0.10)}`,
    ];

    const pill = `<div id="${pillId}" style="${pillStyle.join(";")};">${escapeHtml(label)}</div>`;

    return `<div id="${unitId}" style="${wrapStyle.join(";")};">${iconWrap}${pill}</div>`;
  }

  const pillsHtml = items
    .map((it, i) =>
      renderUnit(
        i,
        it.label,
        it.tier,
        resolveAppKey(it.label, it.appKey),
        slots[i] ?? slots[slots.length - 1]!,
      ),
    )
    .join("");

  // Diffuser Halo hinter der Headline — klärt die Hierarchie, ohne dass ein
  // sichtbarer Kasten um die Headline gezogen wird.
  const haloSize = isVertical ? 760 : 880;
  const haloHtml = `<div id="${haloId}" style="position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:${haloSize}px;height:${Math.round(haloSize * 0.85)}px;border-radius:9999px;background:radial-gradient(ellipse at 50% 50%, ${withAlpha(themeColors.bg, 0.62)} 0%, ${withAlpha(themeColors.bg, 0.42)} 32%, transparent 70%);filter:blur(22px);pointer-events:none;"></div>`;

  const headlinePx = isVertical ? Math.round(lg * 0.85) : Math.round(lg * 0.9);
  const subPx = isVertical ? Math.round(md * 0.52) : Math.round(md * 0.58);

  // Headline + Subtext stehen FREI (kein Kasten). Subtile text-shadow gibt
  // Tiefe, ohne dass ein sichtbarer Rahmen entsteht.
  const headBlockHtml = `<div style="position:relative;display:flex;flex-direction:column;align-items:center;gap:${isVertical ? 12 : 16}px;text-align:center;pointer-events:none;max-width:${isVertical ? 720 : 920}px;">
    ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${headlinePx}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.0;text-wrap:balance;text-shadow:0 2px 24px ${withAlpha("#000000", 0.6)};">${escapeHtml(headline)}</div>` : ""}
    ${sub ? `<div id="${subId}" style="font-family:'Inter',sans-serif;font-weight:${weights.medium};font-size:${subPx}px;letter-spacing:0.2px;color:${withAlpha(themeColors.fg, 0.85)};line-height:1.35;max-width:${isVertical ? 600 : 740}px;text-shadow:0 1px 12px ${withAlpha("#000000", 0.55)};">${escapeHtml(sub)}</div>` : ""}
  </div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">
    ${haloHtml}
    <div style="position:absolute;inset:0;">
      ${pillsHtml}
    </div>
    <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:${isVertical ? "0 56px" : "0 120px"};pointer-events:none;">
      ${headBlockHtml}
    </div>
  </div>`;

  // ------------------------------------------------------------------
  // Timeline
  // ------------------------------------------------------------------
  const init = [
    gsapSetLine(`#${haloId}`, { opacity: 0, scale: 0.85 }),
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 14 }) : "",
    sub ? gsapSetLine(`#${subId}`, { opacity: 0, y: 10 }) : "",
    // Die Unit (Icon + Pill) animiert gemeinsam: scale-up + fade-in.
    ...unitIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, scale: 0.78, y: 12 })),
    // Das App-Icon bekommt zusätzlich einen kleinen "Snap"-Akzent (skaliert
    // beim Eintritt etwas stärker, damit das quadratische Icon Aufmerksamkeit
    // zieht und die App-Identität sofort liest).
    ...iconIds.map((id) => gsapSetLine(`#${id}`, { scale: 0.6, rotation: -8, transformOrigin: "50% 50%" })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [];

  // 1) Halo zuerst — etabliert die Bühne.
  tweens.push(t.to(`#${haloId}`, { opacity: 1, scale: 1, duration: 0.9, ease: "raw:\"power2.out\"" }, 0.0));

  // 2) Headline + Subtext als ruhiger Anker in der Mitte.
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.6, ease: "raw:\"power3.out\"" }, 0.15));
  if (sub) tweens.push(t.to(`#${subId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.35));

  // 3) Units (Icon + Pill) tauchen leicht versetzt um die Headline herum
  //    auf — keine Rotation der Unit, kein 3D-Tilt. Nur fade + scale +
  //    sanftes y-Lift. Das App-Icon snappt mit eigenem Back-Out, damit das
  //    Quadrat als Icon "klickt".
  unitIds.forEach((id, i) => {
    const slot = slots[i] ?? slots[slots.length - 1]!;
    const at = 0.45 + slot.delay;
    tweens.push(t.to(`#${id}`, {
      opacity: 1, scale: 1, y: 0,
      duration: 0.5, ease: "raw:\"back.out(1.4)\"",
    }, at));
    tweens.push(t.to(`#${iconIds[i]}`, {
      scale: 1, rotation: 0,
      duration: 0.55, ease: "raw:\"back.out(2.2)\"",
    }, at + 0.05));

    // 4) Sanftes Schweben (nur vertikal) — auf der gemeinsamen Unit, damit
    //    Icon und Pill als geschlossene Karte atmen.
    const floatStart = Math.max(0, at + 0.35 - slot.phase);
    const halfPeriod = 1.4;
    tweens.push(t.to(`#${id}`, {
      y: slot.driftY,
      duration: halfPeriod,
      repeat: -1,
      yoyo: true,
      ease: "raw:\"sine.inOut\"",
    }, floatStart));
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
